import { ClientConstants } from './../../../../formbird-services/src/lib/constants/ClientConstants';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {ComponentDefinition} from '@formbird/types';
import {HttpClient} from '@angular/common/http';
import {FormbirdInjectorService} from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import {SharedUrlRoutes, UtilValidation} from '@formbird/shared';
import {debounceTime} from 'rxjs/operators';
import {ChangedDocumentService} from 'formbird-services';
import {Validators} from '@angular/forms';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AbstractComponent, MyErrorStateMatcher} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';

const logger = console;

// extend the ComponentDefinition type with the additional fields for the component
interface PasswordEntryComponentDefinition extends ComponentDefinition {
  requireOriginalPassword?: any;
}

interface PasswordEntryValue {
  existingPass?: any;
  newPass?: any;
  confirmPass?: any;
  email?: any;
  requireOriginalPassword?: any;
}

@Component({
  templateUrl: './password-entry.component.html',
  styleUrls: ['./password-entry.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PasswordEntryComponent extends AbstractComponent<PasswordEntryComponentDefinition> implements OnInit {
  @Input() account: any;

  notificationService: any;
  modifiedFieldService: any;

  formControlExistingPassword: any;
  formControlNewPassword: any;
  formControlConfirmPassword: any;

  matcherExistingPassword = new MyErrorStateMatcher();
  matcherNewPassword = new MyErrorStateMatcher();
  matcherConfirmPassword = new MyErrorStateMatcher();

  passwordEntries: PasswordEntryValue;
  passwordValidationErrMsg: any;
  loginData;
  isAccepted = false;
  validationError = null;
  isReadOnly;
  existingPassEntered;
  newPassEntered;
  requireOriginalPassword;
  validatorMsg;
  hidePassword = true;
  hideConfirmPassword = true;
  hideOriginalPassword = true;

  constructor(
    private httpClient: HttpClient,
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);

    this.notificationService = this.formbirdInjectorService.get('NotificationService');
    this.modifiedFieldService = this.formbirdInjectorService.get('ModifiedFieldService');
    this.validatorMsg = {};
  }

  ngOnInit() {
    this.loginData = {};
    this.passwordEntries = { // stores the passwordEntries. They will be added into the document for saving on valid
      // entry of the existing password and new and confirmed password
      requireOriginalPassword: false
    } as PasswordEntryValue;

    this.componentSetupService.setupStandardProperties(this);

    this.isReadOnly = false;

    // variables for determining whether to enable existing and confirmed password inputs
    this.existingPassEntered = false;
    this.newPassEntered = false;

    // don't require the original password if this is a new document or the requireOriginalPassword option has been
    // specified
    this.requireOriginalPassword = false;

    if (this.fieldValue) {
      if (this.componentDefinition.requireOriginalPassword) {
        this.requireOriginalPassword = true;
        this.passwordEntries.requireOriginalPassword = true;
      }
    }

    if (!UtilValidation.hasValidateFunc(this.componentDefinition)) {
      this.isAccepted = true;
    }

    this.httpClient.get(SharedUrlRoutes.serverRoutes.loggedUser).subscribe(res => {
        let data;
        data = res;
        this.loginData.email = data.user.email;
        this.passwordEntries = { ...this.passwordEntries, email: data.user.email };
      },
      error => {
        logger.error(error);
      });

    this.setupControls();
    this.setFieldActivation();
    this.setupChangeDetection();

    // pass the password entries into valueChanged function so they will be sent to the server and saved in the correct
    // format. All three passwords are stored in the document, but on saving, the password will be validated
    // and stored as a single password
    const existingPassSub = this.formControlExistingPassword.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME))
      .subscribe(value => {
        this.passwordEntries = { ...this.passwordEntries, existingPass: value };
        if (this.passwordEntries.newPass && this.passwordEntries.newPass.length > 0
           && this.passwordEntries.newPass === this.passwordEntries.confirmPass) {
          this.valueChanged(this.passwordEntries);
        }
      });

    const newPassSub = this.formControlNewPassword.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME))
      .subscribe(value => {
        this.passwordEntries = { ...this.passwordEntries, newPass: value};
        if (this.passwordEntries.newPass === this.passwordEntries.confirmPass) {
          this.valueChanged(this.passwordEntries);
        }
      });
    const confirmPassSub = this.formControlConfirmPassword.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME))
      .subscribe(value => {
        this.passwordEntries = { ...this.passwordEntries, confirmPass: value};
        if (this.passwordEntries.newPass === this.passwordEntries.confirmPass) {
          this.valueChanged(this.passwordEntries);
        }
      });

    this.subs.add(existingPassSub);
    this.subs.add(newPassSub);
    this.subs.add(confirmPassSub);

  }

  setupControls() {

    const existPassName = this.registeredControlName + '_existPass';
    this.formControlExistingPassword =
      this.componentSetupService.setupControl(this, existPassName, '', [Validators.required]);

    const newPassName = this.registeredControlName + '_newPass';
    this.formControlNewPassword =
      this.componentSetupService.setupControl(this, newPassName, '', [Validators.required]);

    const confirmPassName = this.registeredControlName + '_confirmPass';
    this.formControlConfirmPassword =
      this.componentSetupService.setupControl(this, confirmPassName, '', [Validators.required]);

  }

  onFieldValueChanged(fieldValue) {
    if (this.fieldValue === fieldValue) {
      return;
    }

    this.fieldValue = fieldValue;

    this.formControlConfirmPassword.patchValue(fieldValue?.confirmPass, { emitEvent: false, onlySelf: true });

    this.formControlExistingPassword.patchValue(fieldValue?.existingPass, { emitEvent: false, onlySelf: true });

    this.formControlNewPassword.patchValue(fieldValue?.newPass, { emitEvent: false, onlySelf: true });

    this.detectChanges();
  }

  setFieldActivation() {
    if (this.disabled) {
      this.formControlExistingPassword.disable({emitEvent: false});
      this.formControlNewPassword.disable({emitEvent: false});
      this.formControlConfirmPassword.disable({emitEvent: false});
    } else {
      this.formControlExistingPassword.enable({emitEvent: false});
      this.formControlNewPassword.enable({emitEvent: false});
      this.formControlConfirmPassword.enable({emitEvent: false});
    }

    this.detectChanges();
  }

  validateExistingPassword(value) {
    if (value == null || value.trim().length == 0) {
      return;
    }
    this.passwordEntries = { ...this.passwordEntries, existingPass: value};
    const self = this;
    if (self.passwordEntries.existingPass && self.passwordEntries.existingPass.length > 0) {
      self.loginData.pass = self.passwordEntries.existingPass;
      if (!self.isAccepted) {
        self.document[self.fieldName] = self.loginData.pass;
        self.isAccepted = UtilValidation.validateFieldValue(self.passwordEntries);
      }

      if (self.isAccepted) {
        self.httpClient.post(SharedUrlRoutes.serverRoutes.login, self.loginData).subscribe(res => {
            let data;
            data = res;
            if (data && typeof data.systemHeader !== null) {
              self.isReadOnly = false;
              self.existingPassEntered = true;
            } else {
              self.notificationService.printMessage('The existing password is incorrect', 'error');
              self.isReadOnly = true;
            }
            if (self.passwordEntries.newPass && self.passwordEntries.newPass.trim().length > 0
             && self.passwordEntries.newPass === self.passwordEntries.confirmPass) {
              self.valueChanged(self.passwordEntries);
            }
          },
          error => {
            logger.error(error);
            self.notificationService.printMessage('The existing password is incorrect', 'error');
            self.isReadOnly = true;
          });
      }

    }
  }

  validateNewPassword(value) {
    if (value == null || value.trim().length == 0) {
      return;
    }
    this.passwordEntries = { ...this.passwordEntries, newPass: value};
    if (this.passwordEntries.newPass && this.passwordEntries.newPass.length > 0 &&
      !UtilValidation.validateFieldValue(this.passwordEntries)) {

      const validator = UtilValidation.getValidatorObj(this.passwordEntries);
      if (validator && validator.validationMsg) {
        this.notificationService.printMessage(validator.validationMsg, 'error');
      } else {
        this.newPassEntered = true;
      }
    }
  }

  newPasswordOnChange() {
    this.newPassEntered = this.passwordEntries.newPass && this.passwordEntries.newPass.length > 0;
  }

  validateConfirmPassword(value) {
    if (value == null || value.trim().length == 0) {
      return;
    }
    this.passwordEntries = { ...this.passwordEntries, confirmPass: value};
    this.validationError = '';
    this.validatorMsg.passwordValidationErrMsg = null;
    if (this.passwordEntries.confirmPass && this.passwordEntries.confirmPass.length > 0 &&
      !UtilValidation.validateFieldValue(this.passwordEntries)) {

      const validator = UtilValidation.getValidatorObj(this.passwordEntries);
      if (validator && validator.validationMsg) {
        this.notificationService.printMessage(validator.validationMsg, 'error');
      }
    }
    if (this.passwordEntries.confirmPass !== this.passwordEntries.newPass) {
      const errorMessage = 'The new and confirmed passwords must match';
      this.validationError = this.validatorMsg.passwordValidationErrMsg;
      this.validatorMsg.passwordValidationErrMsg = errorMessage;

      this.notificationService.printMessage(errorMessage, 'error');
    } else {
      this.validatorMsg.passwordValidationErrMsg = this.validationError;
    }
    if (this.validatorMsg.passwordValidationErrMsg != null && this.validatorMsg.passwordValidationErrMsg.trim().length == 0) {
      this.validatorMsg.passwordValidationErrMsg = null;
    }
  }

  doPasswordChange() {
    const self = this;
    let data;
    data = {};
    data.email = self.account.email;
    data.pass = self.passwordEntries.newPass;
    data.repass = self.passwordEntries.confirmPass;
    const url = 'auth/resetpass';
    self.httpClient.post(url, data).subscribe(res => {
        let response;
        response = res;
        const resData = response.data;
        if (resData.status === 'OK') {
          self.notificationService.success('Reset password success!');
        }
      },
      error => {
        logger.error(error);
      });
  }

  onHidePassword() {
    this.hidePassword = !this.hidePassword;
    this.cdr.detectChanges();
  }
  onHideConfirmPassword() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
    this.cdr.detectChanges();
  }
  onHideOriginalPassword() {
    this.hideOriginalPassword = !this.hideOriginalPassword;
    this.cdr.detectChanges();
  }
}
